import React, { useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { MaskedInput } from 'react-hook-mask'
import { Circles } from 'react-loader-spinner'
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../constants/globalConstants'
import {
    isLoggedIn,
    isPermissionExist,
    requiredTextInput,
    restrictInputValues,
    restrictSpecialChar,
    handleKeyPressRestrictLeadingTrailingSpace,
} from '../../utils/helper'
import PaymentGatewayCommon from '../paymentForm/PaymentGatewayCommon'
import CardConnectTokenizer from '../../components/walletButtons/CardConnectTokenizer'
import WalletButtons from '../../components/walletButtons'

function PaymentModal(props) {
    const {
        show, onHide,
        formData, setFormData,
        facility, prorateResponse,
        isSubmittingForm, setIsSubmittingForm,
        isLoading,
        cardList, setCardFormVisible,
        handleSubmit,
        card, expiry, handleCardChange, handleExpiryChange,
        handleInputFieldsData, cardExpiryMessage,
        consentJSX,
        tokenRef, setCardToken, setIframeErrorMsg,
        guidValue, resValue, validator,
        APILoad,
        walletButtonShow, walletDisable, totalPrice,
        isIOSDevice, applePaySessionRef, notify, dispatch, setIsLoading,
        formError, accessPermissions,
        cardnumbermask, expirymask,
    } = props

    const [step, setStep] = useState(1)
    const [activeTab, setActiveTab] = useState('newCard')
    const [showConfirm, setShowConfirm] = useState(false)
    const pendingSubmitData = useRef(null)

    const showWalletOption = walletButtonShow && Number(totalPrice) > 0
    const hasSavedCards = isLoggedIn() && cardList?.length > 0

    const facilityPaymentTypeId = formData?.selFaclity?.facility_payment_details?.facility_payment_type_id
    const iframeEnabled = facility?.facility_configuration?.is_iframe_enabled
    const isManualForm = iframeEnabled == '0'
    const isFiserv = facilityPaymentTypeId == '3'
    const isIframe = !isManualForm && !isFiserv

    const handleClose = () => {
        setStep(1)
        setActiveTab('newCard')
        setShowConfirm(false)
        onHide()
    }

    const handleBack = () => {
        if (showConfirm) {
            setShowConfirm(false)
        } else {
            setStep(1)
            setActiveTab('newCard')
        }
    }

    // Central submit handler — intercepts for auto-renewal consent confirmation
    const triggerSubmit = (data) => {
        if (formData?.userConsent) {
            pendingSubmitData.current = data || formData
            setShowConfirm(true)
        } else {
            handleSubmit(data || formData)
        }
    }

    const handleStartParking = () => triggerSubmit(formData)

    const showNewCardForm = () => {
        setCardFormVisible(true)
        setFormData({ ...formData, payment_profile_id: '' })
    }

    const selectSavedCard = (selectedCard) => {
        localStorage.setItem('CardNumberLast4', selectedCard?.card_last_four)
        localStorage.setItem('expiration_date', selectedCard?.expiry)
        localStorage.setItem('card_type', selectedCard?.card_type)
        formData.expiry = selectedCard?.expiry
        formData.card_last_four = selectedCard?.card_last_four
        formData.payment_profile_id = selectedCard?.payment_profile_id
        setFormData(formData)
        setCardFormVisible(false)
    }

    const handleSavedCardSelect = (savedCard) => {
        selectSavedCard(savedCard)
        setActiveTab('savedCard')
        setStep(2)
    }

    const startParkingBtn = (disabled) => (
        <button
            type='button'
            className='btn btn-danger w-100 mt-3 fw-bold'
            onClick={handleStartParking}
            disabled={isSubmittingForm || isLoading || disabled}
        >
            Submit
            {(isSubmittingForm || isLoading) && (
                <span className='ms-2'>
                    <Circles height='20' width='20' color='#ffffff' ariaLabel='circles-loading' visible />
                </span>
            )}
        </button>
    )

    const amountDueRow = (
        <div className='d-flex justify-content-between align-items-center py-2 border-top border-bottom'>
            <strong>Amount Due</strong>
            <strong>{prorateResponse?.total_amount ? `$${prorateResponse?.total_amount}` : ''}</strong>
        </div>
    )

    /* ── New Card tab ── */
    const newCardTabContent = (
        <div className='border rounded-3 p-3' style={{ outline: '3px solid #26a8e066' }}>
            <p className='mb-2 text-muted small'>Enter Payment Details</p>
            {amountDueRow}
            <div className='d-flex align-items-center justify-content-center'>
                <div className='p-2 bg-white ps-0 small text-nowrap text-primary'>Credit/Debit Card</div>
                <div className='bg-grey w-100' style={{ height: '2px' }}></div>
            </div>

            {!isFiserv ? (
                <>
                    {/* Iframe gateways (Heartland type 4, DataCap type 2) */}
                    {isIframe && (
                        <>
                            {/* Consent shown above the iframe — covers all gateway types */}
                            {consentJSX}
                            <PaymentGatewayCommon
                                faciltyData={facility}
                                onSubmit={triggerSubmit}
                                setIsSubmittingForm={setIsSubmittingForm}
                                isSubmittingForm={isSubmittingForm || isLoading}
                                previousButton={<></>}
                                setFormData={setFormData}
                                formData={formData}
                                consent={<></>}
                                setValue={() => {}}
                                customGateWay={<></>}
                                APILoad={APILoad}
                                page='permit'
                            />
                        </>
                    )}

                    {/* Manual card form */}
                    {isManualForm && (
                        <>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <div className='mb-3'>
                                        <label className='form-label'>
                                            Card Number <span className='text-danger'>*</span>
                                        </label>
                                        <MaskedInput
                                            maskGenerator={cardnumbermask}
                                            value={card}
                                            type='text'
                                            name='card_pan'
                                            id='card_pan'
                                            required
                                            pattern='[0-9 ]{18,19}'
                                            maxLength='19'
                                            minLength='18'
                                            className='form-control'
                                            onChange={handleCardChange}
                                            placeholder='Enter Card Number'
                                        />
                                        {requiredTextInput(validator?.current, 'card_pan', formData?.card_pan, 'Card Number', 'required')}
                                    </div>
                                </div>
                                <div className='col-sm-6'>
                                    <div className='mb-3'>
                                        <label className='form-label'>
                                            Expiry Date <span className='text-danger'>*</span>
                                        </label>
                                        <MaskedInput
                                            maskGenerator={expirymask}
                                            value={expiry}
                                            type='text'
                                            autoComplete='off'
                                            name='expiration_date'
                                            id='expiration_date'
                                            required
                                            pattern='[0-9 ]{4}'
                                            maxLength='5'
                                            minLength='4'
                                            className='form-control'
                                            onChange={handleExpiryChange}
                                            placeholder='MM/YY'
                                        />
                                        {requiredTextInput(validator?.current, 'expiration_date', formData?.expiration_date, 'Card Expiry')}
                                        <span className='form-text text-danger h-40'>{cardExpiryMessage}</span>
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <div className='mb-3'>
                                        <label className='form-label'>
                                            CVV <span className='text-danger'>*</span>
                                        </label>
                                        <input
                                            onChange={handleInputFieldsData}
                                            autoComplete='off'
                                            className='form-control'
                                            id='standardCardCvv'
                                            maxLength='4'
                                            name='card_card_security_cvx_2'
                                            placeholder='CVV'
                                            type='password'
                                            onKeyPress={(e) => restrictInputValues(e)}
                                            required
                                        />
                                        {requiredTextInput(validator?.current, 'card_card_security_cvx_2', formData?.card_card_security_cvx_2, 'CVV', 'required')}
                                    </div>
                                </div>
                                <div className='col-sm-6'>
                                    <div className='mb-3'>
                                        <label className='form-label'>
                                            Name on Card <span className='text-danger'>*</span>
                                        </label>
                                        <input
                                            onChange={handleInputFieldsData}
                                            autoComplete='off'
                                            name='name'
                                            id='name'
                                            className='form-control'
                                            maxLength={20}
                                            minLength={1}
                                            placeholder='Name on Card'
                                            type='text'
                                            onKeyPress={(e) => handleKeyPressRestrictLeadingTrailingSpace(e)}
                                            required
                                        />
                                        {requiredTextInput(validator?.current, 'name', formData?.name, 'Name', 'required')}
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <div className='mb-3'>
                                        <label className='form-label'>Zip Code</label>
                                        <input
                                            onChange={handleInputFieldsData}
                                            className='form-control'
                                            maxLength='6'
                                            minLength='5'
                                            name='zip_code'
                                            pattern='[0-9]{5,6}'
                                            placeholder='Billing Address Zip Code'
                                            onPaste={(e) => { e.preventDefault(); return false }}
                                            onKeyPress={(e) =>
                                                isPermissionExist(accessPermissions, 'Allow AlfaNumericZipcode')
                                                    ? restrictSpecialChar(e)
                                                    : restrictInputValues(e)
                                            }
                                            type='text'
                                        />
                                        <span className='form-text text-danger h-40'>**Mandatory for Amex Cards only</span>
                                    </div>
                                </div>
                            </div>
                            <input type='hidden' name='XXX_IPG_XXX' id='XXX_IPG_XXX' value='confirm' />
                            <input type='hidden' name='XXX_IPGTRXNO_XXX' id='XXX_IPGTRXNO_XXX' value={guidValue} />
                            <input type='hidden' name='XXX_IPGSESSION_XXX' id='XXX_IPGSESSION_XXX' value={resValue} />
                            {consentJSX}
                            {startParkingBtn(false)}
                        </>
                    )}
                </>
            ) : (
                <>
                    <CardConnectTokenizer
                        setToken={setCardToken}
                        faciltyPaymentDetails={formData?.selFaclity?.facility_payment_details}
                        setIframeErrorMessage={setIframeErrorMsg}
                        tokenRef={tokenRef}
                    />
                    {consentJSX}
                    {startParkingBtn(false)}
                </>
            )}
        </div>
    )

    /* ── Saved Card tab ── */
    const currentSelectedCard = cardList?.find(c => c?.payment_profile_id === formData?.payment_profile_id)

    const cardBrandLabel = (cardType) => {
        const t = (cardType || '').toUpperCase()
        if (t.includes('VISA')) return <span style={{ fontWeight: '900', fontStyle: 'italic', color: '#1a1f71', letterSpacing: '0.02em' }}>VISA</span>
        if (t.includes('MASTER')) return <span style={{ fontWeight: '900', color: '#eb001b' }}>MC</span>
        if (t.includes('AMEX') || t.includes('AMERICAN')) return <span style={{ fontWeight: '700', color: '#007bc1' }}>AMEX</span>
        if (t.includes('DISCOVER')) return <span style={{ fontWeight: '700', color: '#e65c00' }}>DISC</span>
        return <span className='fw-bold'>{cardType || 'CARD'}</span>
    }

    const savedCardTabContent = (
        <div className='border rounded-3 p-3' style={{ outline: '3px solid #26a8e066' }}>
            <p className='mb-2 text-muted'>Select a saved card</p>

            {/* Selected card preview */}
            <div className='d-flex align-items-center p-3 border rounded-3 mb-3' style={{ minHeight: '52px' }}>
                <i className='fa fa-credit-card me-3' style={{ color: '#8592a6', fontSize: '1.1rem' }}></i>
                <span className='text-muted flex-grow-1' style={{ fontSize: '0.9rem' }}>Payment Method</span>
                {currentSelectedCard ? (
                    <span className='d-flex align-items-center gap-1' style={{ fontSize: '0.9rem' }}>
                        {cardBrandLabel(currentSelectedCard.card_type)}
                        <span className='fw-bold ms-1' style={{ color: '#1a2b4a' }}>
                            {currentSelectedCard.card_last_four?.slice(0, 4) || 'XXXX'}
                        </span>
                    </span>
                ) : (
                    <i className='fa fa-credit-card' style={{ color: '#ddd' }}></i>
                )}
            </div>

            {/* Cards list */}
            <div className='d-flex flex-column gap-2 mb-2'>
                {cardList?.map((savedCard, i) => {
                    const isSelected = savedCard?.payment_profile_id === formData?.payment_profile_id
                    return (
                        <div
                            key={i}
                            role='button'
                            className='d-flex align-items-center p-3 border rounded-3'
                            style={{ cursor: 'pointer', background: isSelected ? '#fafbff' : 'white' }}
                            onClick={() => selectSavedCard(savedCard)}
                        >
                            {/* Checkmark column */}
                            <div style={{ width: '22px', marginRight: '6px', flexShrink: 0 }}>
                                {isSelected && (
                                    <i className='fa fa-check' style={{ color: '#5c6bc0', fontSize: '0.85rem' }}></i>
                                )}
                            </div>
                            {/* Brand label */}
                            <div style={{ minWidth: '44px', marginRight: '10px', fontSize: '0.85rem' }}>
                                {cardBrandLabel(savedCard?.card_type)}
                            </div>
                            {/* Card details */}
                            <div className='flex-grow-1'>
                                <div className='fw-semibold' style={{ fontSize: '0.9rem', color: '#1a2b4a' }}>
                                    Card ending in {savedCard?.card_last_four?.slice(0, 4) || 'XXXX'}
                                </div>
                                <div className='text-muted' style={{ fontSize: '0.8rem' }}>
                                    Expires {savedCard?.expiry || '--'}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            {formError == 'pass' && !formData?.payment_profile_id && (
                <span className='text-danger small'>Please select a saved card</span>
            )}

            
            {amountDueRow}
            {consentJSX}
            {startParkingBtn(!formData?.payment_profile_id)}
        </div>
    )

    /* ── Wallet tab ── */
    const walletTabContent = (
        <div className='text-center'>
            <div className='d-flex justify-content-center gap-3 mb-3 mt-2'>
                <div className='rounded-3 p-3' style={{ background: '#f0f0f0' }}>
                    <i className='fa fa-credit-card fa-2x text-secondary'></i>
                </div>
                <div className='rounded-3 p-3' style={{ background: '#f0f0f0' }}>
                    <i className='fa fa-mobile-alt fa-2x text-secondary'></i>
                </div>
            </div>
            <h5 className='fw-bold'>Secure checkout with<br />Apple Pay / Google Pay</h5>
            <p className='text-muted small mb-3'>You will be redirected to complete your payment securely.</p>
            
            {amountDueRow}
            
            <div className='position-relative mt-3'>
                {(walletDisable || isLoading) && <div className='overlay-opacity'>&nbsp;</div>}
                <WalletButtons
                    key={totalPrice}
                    faciltyData={formData?.selFaclity}
                    getValues={() => formData}
                    onSubmit={handleSubmit}
                    isSubmittingForm={isLoading}
                    setIsSubmittingForm={setIsSubmittingForm}
                    setIsLoading={setIsLoading}
                    payableAmount={totalPrice}
                    notify={notify}
                    isIOSDevice={isIOSDevice}
                    applePaySessionRef={applePaySessionRef}
                    dispatch={dispatch}
                />
            </div>
            <small className='border border-danger p-2 rounded-3 d-block text-small text-danger small w-100 mt-2'>
                By using Apple Pay/GPay, the permit cannot be auto renewed.
            </small>
        </div>
    )

    /* ── Inline booking confirmation (replaces stacked AutoRenewal modal) ── */
    const confirmationView = (
        <div className='text-center py-2'>
            <div
                className='rounded-circle d-inline-flex align-items-center justify-content-center mb-3'
                style={{ width: '56px', height: '56px', background: '#eef2ff' }}
            >
                <i className='fa fa-calendar-check fa-lg' style={{ color: '#5c6bc0' }}></i>
            </div>
            <p className='text-muted small mb-4 px-2'>
                {prorateResponse?.show_auto_consent == '1'
                    ? prorateResponse?.show_auto_consent_msg
                    : 'When purchasing the permit, you opted for auto-renewal, which means your permit will automatically renew on the 1st of each cycle, with the renewal amount being charged to your saved payment method on that day. If you wish to change this setting, please uncheck the auto-renewal option on the previous screen before finalizing your purchase.'
                }
            </p>
            <hr />
            <div className='d-flex justify-content-between mb-3'>
                <strong>Amount Due</strong>
                <strong>{prorateResponse?.total_amount ? `$${prorateResponse?.total_amount}` : ''}</strong>
            </div>
            <hr className='mb-4' />
            <div className='d-flex gap-3 justify-content-center'>
                <button
                    type='button'
                    className='btn btn-secondary px-4'
                    onClick={() => setShowConfirm(false)}
                    disabled={isSubmittingForm || isLoading}
                >
                    Cancel
                </button>
                <button
                    type='button'
                    className='btn btn-primary px-4 d-flex align-items-center gap-2'
                    onClick={() => handleSubmit(pendingSubmitData.current || formData)}
                    disabled={isSubmittingForm || isLoading}
                >
                    Continue
                    {(isSubmittingForm || isLoading) && (
                        <Circles height='18' width='18' color='#ffffff' ariaLabel='circles-loading' visible />
                    )}
                </button>
            </div>
        </div>
    )

    const tabContent = { newCard: newCardTabContent, savedCard: savedCardTabContent, wallet: walletTabContent }

    const tabDefs = [
        {
            key: 'newCard',
            label: 'New Card',
            icon: 'fa fa-credit-card',
            onClick: () => { showNewCardForm(); setActiveTab('newCard') },
        },
        ...(hasSavedCards ? [{
            key: 'savedCard',
            label: 'Saved Card',
            icon: 'fa fa-credit-card',
            onClick: () => setActiveTab('savedCard'),
        }] : []),
        ...(showWalletOption ? [{
            key: 'wallet',
            label: 'Apple Pay / GPay',
            icon: null,
            onClick: () => setActiveTab('wallet'),
        }] : []),
    ]

    const showBackBtn = step === 2 || showConfirm
    const headerTitle = showConfirm ? 'Confirm Your Booking' : 'Payment Details'
    // Lock the modal while an API call is running from the confirmation view
    const isApiRunning = showConfirm && (isSubmittingForm || isLoading)

    return (
        <Modal
            show={show}
            onHide={isApiRunning ? undefined : handleClose}
            backdrop='static'
            keyboard={!isApiRunning}
            centered
            size='lg'
            dialogClassName="tfpm-dialog"
            contentClassName="tfpm-content"
        >
            <Modal.Header style={{ borderBottom: 'none', paddingBottom: 0, flexDirection: 'column', alignItems: 'stretch' }}>
                {/* Title row */}
                <div className='d-flex align-items-center' style={{ marginBottom: (step === 2 && !showConfirm) ? '0.5rem' : '0.25rem' }}>
                    <button
                        type='button'
                        onClick={(!isApiRunning && showBackBtn) ? handleBack : undefined}
                        disabled={isApiRunning}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: (showBackBtn && !isApiRunning) ? '#6b7280' : 'transparent',
                            fontSize: '1rem',
                            padding: '0',
                            marginRight: '0.5rem',
                            cursor: (showBackBtn && !isApiRunning) ? 'pointer' : 'default',
                            minWidth: '24px',
                            lineHeight: 1,
                        }}
                    >
                        &#8592;
                    </button>
                    <div className='fw-bold text-center flex-grow-1' style={{ fontSize: '1.1rem', color: '#1a2b4a' }}>
                        {headerTitle}
                    </div>
                    <button
                        type='button'
                        onClick={isApiRunning ? undefined : handleClose}
                        disabled={isApiRunning}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: isApiRunning ? '#ccc' : '#6b7280',
                            fontSize: '1.25rem',
                            padding: '0',
                            marginLeft: '0.5rem',
                            cursor: isApiRunning ? 'not-allowed' : 'pointer',
                            lineHeight: 1,
                        }}
                    >
                        &#10005;
                    </button>
                </div>

                {/* Tabs row — Step 2 only, hidden when showing inline confirmation */}
                {step === 2 && !showConfirm && (
                    <div style={{ display: 'flex', borderBottom: '1px solid grey' }}>
                        {tabDefs.map((tab) => {
                            const isActive = activeTab === tab.key
                            return (
                                <button
                                    key={tab.key}
                                    type='button'
                                    onClick={tab.onClick}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        borderBottom: isActive ? '2px solid #1a2b4a' : '2px solid transparent',
                                        color: isActive ? '#1a2b4a' : '#9ca3af',
                                        fontWeight: isActive ? '600' : '400',
                                        fontSize: '0.9rem',
                                        padding: '0.4rem 1rem',
                                        marginBottom: '-2px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {tab.icon && <i className={tab.icon}></i>}
                                    {tab.label}
                                </button>
                            )
                        })}
                    </div>
                )}
            </Modal.Header>

            <Modal.Body>
                {showConfirm ? (
                    confirmationView
                ) : step === 1 ? (
                    /* ── Step 1: choose payment method ── */
                    <div>
                        <div className='text-center text-muted mb-4 small'>
                            <i className='fa fa-lock me-1'></i>
                            Your payment information is secure and encrypted
                        </div>
                        <h5 className='fw-bold mb-1'>How would you like to pay?</h5>
                        <p className='text-muted small mb-4'>Choose a payment method to continue.</p>

                        <div className='d-flex flex-column gap-3'>
                            <div
                                role='button'
                                className='d-flex align-items-center p-3 border rounded-3'
                                style={{ cursor: 'pointer' }}
                                onClick={() => { showNewCardForm(); setActiveTab('newCard'); setStep(2) }}
                            >
                                <div
                                    className='rounded-3 p-2 me-3 d-flex align-items-center justify-content-center'
                                    style={{ background: '#fff0f0', width: '48px', height: '48px' }}
                                >
                                    <i className='fa fa-credit-card text-danger fa-lg'></i>
                                </div>
                                <div className='flex-grow-1'>
                                    <div className='fw-bold'>New Card</div>
                                    <div className='text-muted small'>Pay using a new card</div>
                                </div>
                                <i className='fa fa-chevron-right text-muted'></i>
                            </div>

                            {hasSavedCards && cardList?.map((savedCard, i) => (
                                <div
                                    key={i}
                                    role='button'
                                    className='d-flex align-items-center p-3 border rounded-3'
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSavedCardSelect(savedCard)}
                                >
                                    <div
                                        className='rounded-3 p-2 me-3 d-flex align-items-center justify-content-center'
                                        style={{ background: '#eef2ff', width: '48px', height: '48px' }}
                                    >
                                        <i className='fa fa-credit-card fa-lg' style={{ color: '#5c6bc0' }}></i>
                                    </div>
                                    <div className='flex-grow-1'>
                                        <div className='fw-bold'>Saved Card</div>
                                        <div className='text-muted small'>
                                            {savedCard?.card_type ? `${savedCard.card_type} ` : ''}
                                            {`••••${savedCard?.card_last_four?.slice(0, 4) || 'XXXX'}`}
                                        </div>
                                    </div>
                                    <i className='fa fa-chevron-right text-muted'></i>
                                </div>
                            ))}

                            {showWalletOption && (
                                <div
                                    role='button'
                                    className='d-flex align-items-center p-3 border rounded-3'
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => { setActiveTab('wallet'); setStep(2) }}
                                >
                                    <div
                                        className='rounded-3 p-2 me-3 d-flex align-items-center justify-content-center'
                                        style={{ background: '#e8f5e9', width: '48px', height: '48px', lineHeight: 1.1 }}
                                    >
                                        <span className='fw-bold text-success' style={{ fontSize: '0.65rem' }}>
                                            G Pay<br />◼ Pay
                                        </span>
                                    </div>
                                    <div className='flex-grow-1'>
                                        <div className='fw-bold'>Apple Pay / Google Pay</div>
                                        <div className='text-muted small'>Pay securely using your wallet</div>
                                    </div>
                                    <i className='fa fa-chevron-right text-muted'></i>
                                </div>
                            )}
                        </div>

                        <div className='text-center mt-4 text-muted small'>
                            <i className='fa fa-shield-alt me-1'></i>
                            <strong>100% Secure Payments</strong>
                            <div>Powered by trusted payment partners</div>
                        </div>
                    </div>
                ) : (
                    /* ── Step 2: tab content ── */
                    <div>{tabContent[activeTab]}</div>
                )}
            </Modal.Body>
        </Modal>
    )
}

PaymentModal.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    formData: PropTypes.object,
    setFormData: PropTypes.func,
    facility: PropTypes.object,
    prorateResponse: PropTypes.object,
    isSubmittingForm: PropTypes.bool,
    setIsSubmittingForm: PropTypes.func,
    isLoading: PropTypes.bool,
    cardList: PropTypes.array,
    isCardFormVisible: PropTypes.bool,
    setCardFormVisible: PropTypes.func,
    handleSubmit: PropTypes.func,
    handleModalTerm: PropTypes.func,
    card: PropTypes.string,
    expiry: PropTypes.string,
    handleCardChange: PropTypes.func,
    handleExpiryChange: PropTypes.func,
    handleInputFieldsData: PropTypes.func,
    cardExpiryMessage: PropTypes.string,
    consentJSX: PropTypes.element,
    tokenRef: PropTypes.object,
    setCardToken: PropTypes.func,
    setIframeErrorMsg: PropTypes.func,
    guidValue: PropTypes.string,
    resValue: PropTypes.string,
    validator: PropTypes.object,
    APILoad: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    walletButtonShow: PropTypes.bool,
    walletDisable: PropTypes.bool,
    totalPrice: PropTypes.number,
    isIOSDevice: PropTypes.bool,
    applePaySessionRef: PropTypes.object,
    notify: PropTypes.func,
    dispatch: PropTypes.func,
    setIsLoading: PropTypes.func,
    handleCardSelectChange: PropTypes.func,
    formError: PropTypes.string,
    accessPermissions: PropTypes.string,
    cardnumbermask: PropTypes.object,
    expirymask: PropTypes.object,
}

PaymentModal.defaultProps = {
    show: false,
    onHide: noop,
    formData: EMPTY_OBJECT,
    setFormData: noop,
    facility: EMPTY_OBJECT,
    prorateResponse: EMPTY_OBJECT,
    isSubmittingForm: false,
    setIsSubmittingForm: noop,
    isLoading: false,
    cardList: EMPTY_ARRAY,
    isCardFormVisible: false,
    setCardFormVisible: noop,
    handleSubmit: noop,
    handleModalTerm: noop,
    card: '',
    expiry: '',
    handleCardChange: noop,
    handleExpiryChange: noop,
    handleInputFieldsData: noop,
    cardExpiryMessage: '',
    consentJSX: null,
    tokenRef: null,
    setCardToken: noop,
    setIframeErrorMsg: noop,
    guidValue: '',
    resValue: '',
    validator: null,
    APILoad: null,
    walletButtonShow: false,
    walletDisable: false,
    totalPrice: 0,
    isIOSDevice: false,
    applePaySessionRef: null,
    notify: noop,
    dispatch: noop,
    setIsLoading: noop,
    handleCardSelectChange: noop,
    formError: '',
    accessPermissions: '',
    cardnumbermask: null,
    expirymask: null,
}

export default PaymentModal
